import { create } from 'zustand';
import type { Socket } from 'socket.io-client';
import { CallManager, type CallKind, type CallState } from '@/services/webrtc';

interface CallRuntime {
  kind: CallKind;
  direction: 'incoming' | 'outgoing';
  callId: string;
  remoteUserId: string;
  remoteUserName: string;
  state: CallState;
  startedAt: number;
  /** SDP offer received from caller (incoming only) — applied after accept */
  pendingOffer?: RTCSessionDescriptionInit;
}

interface CallStore {
  /** Current call state (null when idle). */
  current: CallRuntime | null;
  manager: CallManager | null;
  localStream: MediaStream | null;
  remoteStream: MediaStream | null;
  muted: boolean;
  videoOff: boolean;

  incoming: (args: {
    callId: string;
    kind: CallKind;
    remoteUserId: string;
    remoteUserName: string;
  }) => void;

  startOutgoing: (args: {
    socket: Socket;
    kind: CallKind;
    remoteUserId: string;
    remoteUserName: string;
    localUserId: string;
    localUserName: string;
    conversationId?: string;
  }) => Promise<void>;

  accept: (socket: Socket, localUserId: string) => Promise<void>;
  reject: (socket: Socket, localUserId: string) => void;
  hangup: () => void;

  setMuted: (m: boolean) => void;
  setVideoOff: (o: boolean) => void;
}

function newCallId(): string {
  return `call-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

export const useCallStore = create<CallStore>((set, get) => ({
  current: null,
  manager: null,
  localStream: null,
  remoteStream: null,
  muted: false,
  videoOff: false,

  incoming: (args) => {
    set({
      current: {
        kind: args.kind,
        direction: 'incoming',
        callId: args.callId,
        remoteUserId: args.remoteUserId,
        remoteUserName: args.remoteUserName,
        state: 'ringing',
        startedAt: Date.now(),
      },
      localStream: null,
      remoteStream: null,
      muted: false,
      videoOff: false,
    });
  },

  startOutgoing: async (args) => {
    const callId = newCallId();
    const manager = new CallManager(args.socket, {
      onStateChange: (state) =>
        set((s) => (s.current ? { current: { ...s.current, state } } : {})),
      onLocalStream: (localStream) => set({ localStream }),
      onRemoteStream: (remoteStream) => set({ remoteStream }),
      onError: () => {
        /* CallManager already downgrades state; we just surface via UI */
      },
    });
    set({
      manager,
      current: {
        kind: args.kind,
        direction: 'outgoing',
        callId,
        remoteUserId: args.remoteUserId,
        remoteUserName: args.remoteUserName,
        state: 'ringing',
        startedAt: Date.now(),
      },
      localStream: null,
      remoteStream: null,
      muted: false,
      videoOff: false,
    });
    await manager.startOutgoing({
      callId,
      kind: args.kind,
      localUserId: args.localUserId,
      localUserName: args.localUserName,
      remoteUserId: args.remoteUserId,
      ...(args.conversationId ? { conversationId: args.conversationId } : {}),
    });
  },

  accept: async (socket, localUserId) => {
    const cur = get().current;
    if (!cur || cur.direction !== 'incoming') return;
    const manager = new CallManager(socket, {
      onStateChange: (state) =>
        set((s) => (s.current ? { current: { ...s.current, state } } : {})),
      onLocalStream: (localStream) => set({ localStream }),
      onRemoteStream: (remoteStream) => set({ remoteStream }),
      onError: () => {},
    });
    set({ manager });
    await manager.accept({
      callId: cur.callId,
      kind: cur.kind,
      localUserId,
      remoteUserId: cur.remoteUserId,
    });
  },

  reject: (socket, localUserId) => {
    const cur = get().current;
    if (!cur) return;
    // Create a one-shot manager if we don't have one yet (pre-accept reject)
    if (get().manager) {
      get().manager!.reject(cur.callId, localUserId, cur.remoteUserId);
    } else {
      socket.emit('call:reject', {
        callId: cur.callId,
        from: localUserId,
        to: cur.remoteUserId,
        reason: 'declined',
      });
    }
    set({ current: null, manager: null, localStream: null, remoteStream: null });
  },

  hangup: () => {
    get().manager?.hangup(true);
    set({ current: null, manager: null, localStream: null, remoteStream: null, muted: false, videoOff: false });
  },

  setMuted: (m) => {
    get().manager?.toggleMute(m);
    set({ muted: m });
  },
  setVideoOff: (o) => {
    get().manager?.toggleVideo(o);
    set({ videoOff: o });
  },
}));
